import React, { useEffect, useRef, useState } from 'react';

interface Props {
  /** The raw value from the CMS: "570", "35+", "1.220", "ISO"… */
  value: string;
  duration?: number;
}

const prefersReduced = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Extract a leading/embedded integer while keeping prefix + suffix (e.g. "35+" → 35 with "+"). */
function parse(raw: string) {
  const m = raw.match(/^(\D*)(\d[\d.,\s]*\d|\d)(.*)$/s);
  if (!m) return null;
  const digits = m[2].replace(/[^\d]/g, '');
  const target = parseInt(digits, 10);
  if (!Number.isFinite(target)) return null;
  const sep = /\./.test(m[2]) ? '.' : /,/.test(m[2]) ? ',' : '';
  return { prefix: m[1], suffix: m[3], target, sep };
}

function group(n: number, sep: string) {
  const s = String(n);
  return sep ? s.replace(/\B(?=(\d{3})+(?!\d))/g, sep) : s;
}

/**
 * Renders a statistic number. On first paint (and for no-JS / prerender / reduced-motion)
 * it shows the FINAL value — protecting SEO, LCP and CLS. Once scrolled into view it counts
 * up from zero. Non-numeric values (e.g. "ISO") are rendered verbatim without animation.
 */
const CountUp: React.FC<Props> = ({ value, duration = 1100 }) => {
  const parsed = parse(value);
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState<string>(value);

  useEffect(() => {
    setDisplay(value);
    if (!parsed) return;
    const el = ref.current;
    if (!el || prefersReduced()) return;

    let raf = 0;
    let started = false;
    const finalStr = parsed.prefix + group(parsed.target, parsed.sep) + parsed.suffix;

    const run = () => {
      const t0 = performance.now();
      const tick = (now: number) => {
        const p = Math.min(1, (now - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        const cur = Math.round(parsed.target * eased);
        setDisplay(parsed.prefix + group(cur, parsed.sep) + parsed.suffix);
        if (p < 1) raf = requestAnimationFrame(tick);
        else setDisplay(finalStr);
      };
      raf = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) { started = true; io.disconnect(); run(); }
      });
    }, { threshold: 0.4 });
    io.observe(el);

    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!parsed) return <>{value}</>;
  return <span ref={ref}>{display}</span>;
};

export default CountUp;
